import { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import {
  FaUser,
  FaShoppingBag,
  FaHeart,
  FaMapMarkerAlt,
  FaBell,
  FaHeadset,
  FaSignOutAlt,
  FaCog,
  FaEdit,
  FaPlus,
  FaClock,
  FaFileAlt,
  FaStethoscope,
  FaPills,
  FaVial,
  FaVideo,
  FaHistory,
  FaChevronRight,
  FaShieldAlt,
  FaWallet,
  FaGift
} from 'react-icons/fa';
import api from '../api/axios';

export default function Profile() {
  const { token, logout } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    orders: 0,
    addresses: 0,
    consultations: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchProfileData();
  }, [token]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/profile/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data.user || response.data);

      // Fetch stats
      try {
        const [ordersRes, appointmentsRes, testsRes, paramedicalRes, addressesRes, consultationsRes] = await Promise.all([
          api.get('/orders/history/?limit=1', { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: { pagination: {}}})),
          api.get('/appointments/history/?limit=1', { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: { pagination: {}}})),
          api.get('/medical-tests/appointments/history/?limit=1', { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: { pagination: {}}})),
          api.get('/paramedical-services/appointments/history/?limit=1', { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: { pagination: {}}})),
          api.get('/addresses/', { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: { data: [] }})),
          api.get('/consultations/history/?limit=1', { headers: { Authorization: `Bearer ${token}` }}).catch(() => ({ data: { pagination: {}}}))
        ]);

        const getTotal = (res) => {
          const pg = res.data?.pagination || {};
          return pg.total_count || pg.total || 0;
        };

        setStats({
          orders: getTotal(ordersRes) + getTotal(appointmentsRes) + getTotal(testsRes) + getTotal(paramedicalRes),
          addresses: Array.isArray(addressesRes.data?.data) ? addressesRes.data.data.length : (Array.isArray(addressesRes.data?.addresses) ? addressesRes.data.addresses.length : (Array.isArray(addressesRes.data) ? addressesRes.data.length : 0)),
          consultations: getTotal(consultationsRes)
        });
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <>
        <Header />
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p style={{ marginTop: '20px', color: '#666' }}>Loading your profile...</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <style>{`
        .profile-name {
          color: #fff;
        }
        @media (max-width: 600px) {
          .profile-name {
            color: #104a5f;
          }
        }
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 8px !important;
          }
          .stats-grid .stat-card {
            padding: 12px 8px !important;
            flex-direction: column !important;
            align-items: center !important;
            gap: 6px !important;
            text-align: center;
          }
          .stats-grid .stat-icon {
            width: 40px !important;
            height: 40px !important;
          }
          .stats-grid .stat-number {
            font-size: 20px !important;
          }
          .stats-grid .stat-label {
            font-size: 11px !important;
          }
          .services-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
          }
          .services-grid a {
            padding: 16px 10px !important;
          }
        }
      `}</style>
      <Header />
      <div style={styles.container}>
        {/* Profile Header Card */}
        <div style={styles.headerCard}>
          <div style={styles.headerBg}></div>
          <div style={styles.headerContent}>
            <div style={styles.profileImageWrapper}>
              <img
                src={profile?.profile_pic || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(profile?.username || 'User') + '&size=120&background=104a5f&color=fff'}
                alt="Profile"
                style={styles.profileImage}
              />
              <button style={styles.editImageBtn}>
                <FaEdit size={12} />
              </button>
            </div>
            <div style={styles.profileInfo}>
              <h1 style={styles.profileName} className="profile-name">{profile?.username || 'User'}</h1>
              <p style={styles.profileEmail}>{profile?.email || profile?.mobile}</p>
              <p style={styles.profilePhone}>📱 {profile?.mobile}</p>
            </div>
            <button onClick={handleLogout} style={styles.logoutBtn}>
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div style={styles.statsGrid} className="stats-grid">
          <div style={styles.statCard} className="stat-card">
            <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} className="stat-icon">
              <FaShoppingBag color="#fff" size={24} />
            </div>
            <div>
              <div style={styles.statNumber} className="stat-number">{stats.orders}</div>
              <div style={styles.statLabel} className="stat-label">Total Orders</div>
            </div>
          </div>
          <div style={styles.statCard} className="stat-card">
            <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }} className="stat-icon">
              <FaMapMarkerAlt color="#fff" size={24} />
            </div>
            <div>
              <div style={styles.statNumber} className="stat-number">{stats.addresses}</div>
              <div style={styles.statLabel} className="stat-label">Saved Addresses</div>
            </div>
          </div>
          <div style={styles.statCard} className="stat-card">
            <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }} className="stat-icon">
              <FaStethoscope color="#fff" size={24} />
            </div>
            <div>
              <div style={styles.statNumber} className="stat-number">{stats.consultations}</div>
              <div style={styles.statLabel} className="stat-label">Consultations</div>
            </div>
          </div>
        </div>

        {/* Healthcare Services Quick Access */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Healthcare Services</h2>
          <div style={styles.servicesGrid} className="services-grid">
            <Link to="/order-history" style={{ ...styles.serviceCard, background: 'linear-gradient(135deg, #104a5f 0%, #0d3b4a 100%)' }}>
              <FaHistory size={28} color="#fff" />
              <h3 style={styles.serviceTitle}>Order History</h3>
              <p style={styles.serviceDesc}>View all your orders & bookings</p>
            </Link>
            <Link to="/medicines" style={{ ...styles.serviceCard, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <FaPills size={28} color="#fff" />
              <h3 style={styles.serviceTitle}>Medicines</h3>
              <p style={styles.serviceDesc}>Order medicines online</p>
            </Link>
            <Link to="/medical-tests" style={{ ...styles.serviceCard, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
              <FaVial size={28} color="#fff" />
              <h3 style={styles.serviceTitle}>Lab Tests</h3>
              <p style={styles.serviceDesc}>Book medical tests at home</p>
            </Link>
            <Link to="/online-consultation" style={{ ...styles.serviceCard, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
              <FaVideo size={28} color="#fff" />
              <h3 style={styles.serviceTitle}>Consultations</h3>
              <p style={styles.serviceDesc}>Talk to doctors online</p>
            </Link>
          </div>
        </div>

        {/* Account Management */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Account Management</h2>
          <div style={styles.menuList}>
            <Link to="/user/addresses" style={styles.menuItem}>
              <div style={styles.menuItemLeft}>
                <div style={{ ...styles.menuIcon, background: '#e3f2fd' }}>
                  <FaMapMarkerAlt color="#1976d2" size={20} />
                </div>
                <div>
                  <div style={styles.menuItemTitle}>Manage Addresses</div>
                  <div style={styles.menuItemSubtitle}>Add or edit delivery addresses</div>
                </div>
              </div>
              <FaChevronRight color="#ccc" size={16} />
            </Link>

            <Link to="/prescriptions" style={styles.menuItem}>
              <div style={styles.menuItemLeft}>
                <div style={{ ...styles.menuIcon, background: '#f3e5f5' }}>
                  <FaFileAlt color="#9c27b0" size={20} />
                </div>
                <div>
                  <div style={styles.menuItemTitle}>My Prescriptions</div>
                  <div style={styles.menuItemSubtitle}>View & upload prescriptions</div>
                </div>
              </div>
              <FaChevronRight color="#ccc" size={16} />
            </Link>

            <Link to="/chat-consultations" style={styles.menuItem}>
              <div style={styles.menuItemLeft}>
                <div style={{ ...styles.menuIcon, background: '#e8f5e9' }}>
                  <FaVideo color="#4caf50" size={20} />
                </div>
                <div>
                  <div style={styles.menuItemTitle}>My Consultations</div>
                  <div style={styles.menuItemSubtitle}>View chat & video consultations</div>
                </div>
              </div>
              <FaChevronRight color="#ccc" size={16} />
            </Link>

            <div style={styles.menuItem} onClick={() => alert('Coming soon!')}>
              <div style={styles.menuItemLeft}>
                <div style={{ ...styles.menuIcon, background: '#fff3e0' }}>
                  <FaWallet color="#ff9800" size={20} />
                </div>
                <div>
                  <div style={styles.menuItemTitle}>Wallet & Payments</div>
                  <div style={styles.menuItemSubtitle}>Manage payment methods</div>
                </div>
              </div>
              <FaChevronRight color="#ccc" size={16} />
            </div>

            <div style={styles.menuItem} onClick={() => alert('Coming soon!')}>
              <div style={styles.menuItemLeft}>
                <div style={{ ...styles.menuIcon, background: '#fce4ec' }}>
                  <FaGift color="#e91e63" size={20} />
                </div>
                <div>
                  <div style={styles.menuItemTitle}>Rewards & Offers</div>
                  <div style={styles.menuItemSubtitle}>View your rewards points</div>
                </div>
              </div>
              <FaChevronRight color="#ccc" size={16} />
            </div>
          </div>
        </div>

        {/* Settings */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Settings & Privacy</h2>
          <div style={styles.menuList}>
            <div style={styles.menuItem} onClick={() => alert('Coming soon!')}>
              <div style={styles.menuItemLeft}>
                <div style={{ ...styles.menuIcon, background: '#e3f2fd' }}>
                  <FaUser color="#1976d2" size={20} />
                </div>
                <div>
                  <div style={styles.menuItemTitle}>Edit Profile</div>
                  <div style={styles.menuItemSubtitle}>Update your personal information</div>
                </div>
              </div>
              <FaChevronRight color="#ccc" size={16} />
            </div>

            <div style={styles.menuItem} onClick={() => alert('Coming soon!')}>
              <div style={styles.menuItemLeft}>
                <div style={{ ...styles.menuIcon, background: '#f3e5f5' }}>
                  <FaShieldAlt color="#9c27b0" size={20} />
                </div>
                <div>
                  <div style={styles.menuItemTitle}>Privacy & Security</div>
                  <div style={styles.menuItemSubtitle}>Manage your privacy settings</div>
                </div>
              </div>
              <FaChevronRight color="#ccc" size={16} />
            </div>

            <div style={styles.menuItem} onClick={() => alert('Coming soon!')}>
              <div style={styles.menuItemLeft}>
                <div style={{ ...styles.menuIcon, background: '#fff3e0' }}>
                  <FaBell color="#ff9800" size={20} />
                </div>
                <div>
                  <div style={styles.menuItemTitle}>Notifications</div>
                  <div style={styles.menuItemSubtitle}>Manage notification preferences</div>
                </div>
              </div>
              <FaChevronRight color="#ccc" size={16} />
            </div>

            <Link to="/contact-us" style={styles.menuItem}>
              <div style={styles.menuItemLeft}>
                <div style={{ ...styles.menuIcon, background: '#e8f5e9' }}>
                  <FaHeadset color="#4caf50" size={20} />
                </div>
                <div>
                  <div style={styles.menuItemTitle}>Help & Support</div>
                  <div style={styles.menuItemSubtitle}>Get help from our team</div>
                </div>
              </div>
              <FaChevronRight color="#ccc" size={16} />
            </Link>
          </div>
        </div>
      </div>
      <Footer />

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .services-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px 16px 60px',
    backgroundColor: '#f8f9fa'
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #104a5f',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  headerCard: {
    position: 'relative',
    background: '#fff',
    borderRadius: '16px',
    marginBottom: '30px',
    boxShadow: '0 4px 20px rgba(16,74,95,0.1)',
    overflow: 'hidden'
  },
  headerBg: {
    height: '120px',
    background: 'linear-gradient(135deg, #104a5f 0%, #0d3b4a 100%)',
    position: 'relative'
  },
  headerContent: {
    padding: '0 24px 24px',
    display: 'flex',
    alignItems: 'flex-end',
    gap: '20px',
    marginTop: '-60px',
    position: 'relative',
    flexWrap: 'wrap'
  },
  profileImageWrapper: {
    position: 'relative'
  },
  profileImage: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    border: '5px solid #fff',
    objectFit: 'cover',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  },
  editImageBtn: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    background: '#104a5f',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    transition: 'transform 0.2s ease'
  },
  profileInfo: {
    flex: 1,
    minWidth: '200px'
  },
  profileName: {
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 8px 0'
  },
  profileEmail: {
    fontSize: '15px',
    color: '#666',
    margin: '4px 0'
  },
  profilePhone: {
    fontSize: '15px',
    color: '#666',
    margin: '4px 0'
  },
  logoutBtn: {
    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 2px 8px rgba(220,38,38,0.3)'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    marginBottom: '30px',
    className: 'stats-grid'
  },
  statCard: {
    background: '#fff',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer'
  },
  statIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  statNumber: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#333',
    marginBottom: '4px'
  },
  statLabel: {
    fontSize: '14px',
    color: '#666'
  },
  section: {
    marginBottom: '30px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#333',
    marginBottom: '16px',
    paddingLeft: '4px'
  },
  servicesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    className: 'services-grid'
  },
  serviceCard: {
    padding: '24px',
    borderRadius: '12px',
    textDecoration: 'none',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '12px',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    cursor: 'pointer'
  },
  serviceTitle: {
    fontSize: '16px',
    fontWeight: '700',
    margin: 0,
    color: '#fff'
  },
  serviceDesc: {
    fontSize: '13px',
    margin: 0,
    opacity: 0.9
  },
  menuList: {
    background: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #f0f0f0',
    textDecoration: 'none',
    color: '#333',
    cursor: 'pointer',
    transition: 'background 0.2s ease'
  },
  menuItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1
  },
  menuIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  menuItemTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '4px'
  },
  menuItemSubtitle: {
    fontSize: '13px',
    color: '#666'
  }
};
